/**
 * Bugün's data (Faz 23, T-181; the native app's TodayPlan, T-173): today's tasks from the goal
 * tree, grouped, and where each sits on the hour timeline. Days and times are in the
 * account's time zone (zonedTime), so a task at 23:30 is on the day the user lives it.
 */
import { Goal, SubGoal, Todo } from '../../services/goalService';
import { addDaysToKey, dayKey, daysBetween } from '../../utils/zonedTime';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '../../components/TodoDetailView/todoDetailFormatters';

export interface TodayItem {
  todo: Todo;
  subGoal: SubGoal;
  goal: Goal;
}

export interface TodayPlan {
  /** Open, due before today; the most overdue first. */
  overdue: TodayItem[];
  /** Open, due today (the running one included); by time, untimed last. */
  today: TodayItem[];
  /** Finished today, the latest first. */
  completed: TodayItem[];
  /** Today's open tasks without a time (the "Saatsiz" shelf). */
  unscheduled: TodayItem[];
  /** Sum of the open tasks' estimates, seconds. */
  plannedSeconds: number;
}

/** Minutes after midnight of an instant in the account's zone, or null. */
export const minutesOfDay = (iso: string | null | undefined): number | null => {
  const wall = toDateTimeLocalValue(iso);
  if (wall.length < 16) return null;
  return Number(wall.slice(11, 13)) * 60 + Number(wall.slice(14, 16));
};

/** The day ('YYYY-MM-DD') an instant falls on in the account's zone. */
export const dayOf = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : dayKey(at);
};

/** "08:15" for minutes after midnight. */
export const clockLabel = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** An instant (ISO, UTC) for [minutes] after midnight on [day] in the account's zone. */
export const instantAt = (day: string, minutes: number): string | null =>
  fromDateTimeLocalValue(`${day}T${clockLabel(minutes)}`);

/** When a task sits on today's timeline: its reminder today, or the running timer's start. */
export const timeOnDay = (todo: Todo, day: string): number | null => {
  if (todo.isStarted && todo.startedAt && dayOf(todo.startedAt) === day) return minutesOfDay(todo.startedAt);
  if (todo.reminderAt && dayOf(todo.reminderAt) === day) return minutesOfDay(todo.reminderAt);
  return null;
};

export const buildTodayPlan = (goals: Goal[], today: string): TodayPlan => {
  const overdue: TodayItem[] = [];
  const dueToday: TodayItem[] = [];
  const completed: TodayItem[] = [];
  for (const goal of goals) {
    for (const subGoal of goal.subGoals ?? []) {
      for (const todo of subGoal.todos ?? []) {
        const item = { todo, subGoal, goal };
        if (todo.completed) {
          if (dayOf(todo.completedAt) === today) completed.push(item);
          continue;
        }
        const due = todo.dueDate ?? null;
        if (todo.isStarted || due === today) dueToday.push(item);
        else if (due && daysBetween(today, due) < 0) overdue.push(item);
      }
    }
  }
  overdue.sort((a, b) => (a.todo.dueDate as string).localeCompare(b.todo.dueDate as string));
  const at = (i: TodayItem) => timeOnDay(i.todo, today) ?? 24 * 60;
  dueToday.sort((a, b) => at(a) - at(b));
  completed.sort((a, b) => (b.todo.completedAt ?? '').localeCompare(a.todo.completedAt ?? ''));
  return {
    overdue,
    today: dueToday,
    completed,
    unscheduled: dueToday.filter((i) => timeOnDay(i.todo, today) === null),
    plannedSeconds: dueToday.reduce((s, i) => s + (i.todo.estimatedSeconds ?? 0), 0),
  };
};

export interface TimelineBlock {
  item: TodayItem;
  /** Minutes after midnight. */
  start: number;
  /** Length in minutes (the estimate, the running time, or 30). */
  length: number;
  running: boolean;
  done: boolean;
}

/** Default block length when a task has no estimate. */
export const DEFAULT_BLOCK_MINUTES = 30;

export const timelineBlocks = (plan: TodayPlan, today: string, now: Date = new Date()): TimelineBlock[] => {
  const blocks: TimelineBlock[] = [];
  for (const item of [...plan.today, ...plan.completed]) {
    const { todo } = item;
    const done = Boolean(todo.completed);
    const start = done
      ? (todo.reminderAt && dayOf(todo.reminderAt) === today ? minutesOfDay(todo.reminderAt) : minutesOfDay(todo.completedAt))
      : timeOnDay(todo, today);
    if (start === null) continue;
    const running = Boolean(todo.isStarted && todo.startedAt && !done);
    const runMinutes = running ? Math.round((now.getTime() - new Date(todo.startedAt as string).getTime()) / 60000) : 0;
    const estimate = todo.estimatedSeconds ? Math.round(todo.estimatedSeconds / 60) : DEFAULT_BLOCK_MINUTES;
    blocks.push({ item, start, length: Math.max(15, running ? Math.max(runMinutes, estimate) : estimate), running, done });
  }
  return blocks.sort((a, b) => a.start - b.start);
};

/** The hours the timeline shows: 07–21, widened to fit every block. */
export const timelineHours = (blocks: TimelineBlock[]): { from: number; to: number } => {
  let from = 7;
  let to = 21;
  for (const b of blocks) {
    from = Math.min(from, Math.floor(b.start / 60));
    to = Math.max(to, Math.min(23, Math.ceil((b.start + b.length) / 60)));
  }
  return { from, to };
};

/** Snaps minutes to a quarter hour inside the day. */
export const snapQuarter = (minutes: number): number => Math.min(23 * 60 + 45, Math.max(0, Math.round(minutes / 15) * 15));

export interface WeekGoal {
  goal: Goal;
  done: number;
  total: number;
}

/** Per goal, this week's tasks (due Monday–Sunday of [today]'s week): done of total. */
export const weekByGoal = (goals: Goal[], today: string): WeekGoal[] => {
  const weekday = (keyToWeekday(today) + 6) % 7; // Monday = 0
  const start = addDaysToKey(today, -weekday);
  const end = addDaysToKey(start, 6);
  const rows: WeekGoal[] = [];
  for (const goal of goals) {
    if (goal.completed) continue;
    let done = 0;
    let total = 0;
    for (const s of goal.subGoals ?? []) {
      for (const t of s.todos ?? []) {
        const due = t.dueDate;
        if (!due || due < start || due > end) continue;
        total += 1;
        if (t.completed) done += 1;
      }
    }
    if (total > 0) rows.push({ goal, done, total });
  }
  return rows.sort((a, b) => b.total - a.total).slice(0, 4);
};

const keyToWeekday = (key: string): number => new Date(`${key}T12:00:00Z`).getUTCDay();
